import { useAuth } from '../../contexts/AuthContext';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import axios from 'axios';
import {
  ArrowLeft,
  BadgeInfo,
  Banknote,
  Printer,
  Check,
  ChevronDown,
  XCircle,
  Download,
  FileText,
  HeartHandshake,
  History,
  Loader2,
  Pencil,
  PencilLine,
  Plus,
  RefreshCw,
  Shield,
  StopCircle,
  User,
  MapPin,
  Calendar,
  Home,
} from 'lucide-react';
import { DashboardHeader, EmptyState, StatCard, containerVariants, itemVariants, formatCurrency, formatDate } from '../dashboards/shared';
import Employee360PensionDocs from './Employee360PensionDocs';

const TAB_IDS = ['overview', 'employee', 'pension', 'bank', 'nominee', 'documents'];

const PENSION_STATUS_LABELS = {
  active: 'ACTIVE',
  stopped: 'STOPPED',
  closed: 'CLOSED',
};

const PENSION_CATEGORY_OPTIONS = [
  { value: 'REGULAR_PENSION',   label: 'Regular Pension' },
  { value: 'FAMILY_PENSION',    label: 'Family Pension' },
  { value: 'SUPERANNUATION',    label: 'Superannuation' },
  { value: 'DISABILITY_PENSION',label: 'Disability Pension' },
  { value: 'DEATH',             label: 'Death' },
  { value: 'OTHER',             label: 'Other' },
];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

const formatApiError = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg || item?.message || (typeof item === 'string' ? item : null))
      .filter(Boolean);
    if (messages.length) {
      return messages.join(' • ');
    }
  }

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (detail && typeof detail === 'object') {
    if (detail.msg) {
      return detail.msg;
    }
    if (detail.message) {
      return detail.message;
    }
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
};

const SectionCard = ({ title, icon: Icon, children, accent = 'bg-slate-700', action }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
    <div className={`flex items-center justify-between gap-2 px-4 py-3 text-white ${accent}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      {action && <div>{action}</div>}
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const INITIAL_NOMINEE = {
  nominee_name: '', relationship: '', date_of_birth: '', gender: 'male',
  mobile: '', aadhaar_number: '', pan_number: '',
  address_line1: '', city: '', state: '', pincode: '',
  benefit_percentage: '', priority: 1, is_primary: false,
  bank_name: '', branch_name: '', account_holder_name: '', account_number: '', ifsc_code: '',
  remarks: '',
};

const NomineeModal = ({ profileId, nominee, onClose, onSaved, auth }) => {
  const isEdit = !!nominee;
  const [form, setForm] = useState(isEdit ? {
    nominee_name: nominee.nominee_name || '',
    relationship: nominee.relationship || '',
    date_of_birth: nominee.date_of_birth || '',
    gender: nominee.gender || 'male',
    mobile: nominee.mobile || '',
    aadhaar_number: nominee.aadhaar_number || '',
    pan_number: nominee.pan_number || '',
    address_line1: nominee.address_line1 || '',
    city: nominee.city || '',
    state: nominee.state || '',
    pincode: nominee.pincode || '',
    benefit_percentage: nominee.benefit_percentage || '',
    priority: nominee.priority || 1,
    is_primary: nominee.is_primary || false,
    bank_name: nominee.bank_name || '',
    branch_name: nominee.branch_name || '',
    account_holder_name: nominee.account_holder_name || '',
    account_number: nominee.account_number || '',
    ifsc_code: nominee.ifsc_code || '',
    remarks: nominee.remarks || '',
  } : { ...INITIAL_NOMINEE });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const F = (field) => ({
    value: form[field] ?? '',
    onChange: (e) => setForm(prev => ({ ...prev, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })),
  });

  const handleSave = async () => {
    if (!form.nominee_name || !form.relationship || !form.date_of_birth || !form.gender || !form.benefit_percentage) {
      setErr('Name, Relationship, DOB, Gender and Benefit % are required.');
      return;
    }
    setSaving(true); setErr('');
    try {
      const payload = { ...form, benefit_percentage: Number(form.benefit_percentage), priority: Number(form.priority) };
      if (isEdit) {
        await axios.put(`/v1/pensioners/nominees/${nominee.id}`, payload, { headers: auth });
      } else {
        await axios.post(`/v1/pensioners/${profileId}/nominees`, payload, { headers: auth });
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to save nominee.');
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500';
  const labelCls = 'space-y-1 text-sm';
  const spanCls = 'font-medium text-slate-700';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
        className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{isEdit ? 'Edit Nominee' : 'Add Nominee'}</h3>
            <p className="text-sm text-slate-500">Fill in nominee details carefully — this affects family pension eligibility.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-slate-100"><XCircle className="h-5 w-5" /></button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto p-5 space-y-6">
          {err && <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{err}</p>}

          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Personal Details</h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className={labelCls}><span className={spanCls}>Nominee Name *</span><input {...F('nominee_name')} className={inputCls} /></label>
              <label className={labelCls}>
                <span className={spanCls}>Relationship *</span>
                <select {...F('relationship')} className={inputCls}>
                  <option value="">Select Relationship</option>
                  <option value="Mother">Mother</option>
                  <option value="Father">Father</option>
                  <option value="Daughter">Daughter</option>
                  <option value="Son">Son</option>
                  <option value="Widow">Widow</option>
                  <option value="Unmarried Daughter">Unmarried Daughter</option>
                  <option value="Minor Son up to 25 years">Minor Son up to 25 years</option>
                </select>
              </label>
              <label className={labelCls}><span className={spanCls}>Date of Birth *</span><input type="date" {...F('date_of_birth')} className={inputCls} /></label>
              <label className={labelCls}>
                <span className={spanCls}>Gender *</span>
                <select {...F('gender')} className={inputCls}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className={labelCls}><span className={spanCls}>Mobile</span><input {...F('mobile')} className={inputCls} /></label>
              <label className={labelCls}><span className={spanCls}>Aadhaar</span><input {...F('aadhaar_number')} className={inputCls} /></label>
              <label className={labelCls}><span className={spanCls}>PAN</span><input {...F('pan_number')} className={inputCls} /></label>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Address</h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className={`${labelCls} md:col-span-2`}><span className={spanCls}>Address</span><input {...F('address_line1')} className={inputCls} /></label>
              <label className={labelCls}><span className={spanCls}>City</span><input {...F('city')} className={inputCls} /></label>
              <label className={labelCls}><span className={spanCls}>State</span><input {...F('state')} className={inputCls} /></label>
              <label className={labelCls}><span className={spanCls}>Pincode</span><input {...F('pincode')} className={inputCls} /></label>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Benefit & Priority</h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className={labelCls}><span className={spanCls}>Benefit % *</span><input type="number" min="1" max="100" {...F('benefit_percentage')} className={inputCls} /></label>
              <label className={labelCls}><span className={spanCls}>Priority</span><input type="number" min="1" {...F('priority')} className={inputCls} /></label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm(p => ({ ...p, is_primary: e.target.checked }))} className="h-4 w-4 rounded" />
                <span className="font-medium text-slate-700">Primary Nominee</span>
              </label>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Bank Details</h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className={labelCls}><span className={spanCls}>Bank Name</span><input {...F('bank_name')} className={inputCls} /></label>
              <label className={labelCls}><span className={spanCls}>Branch</span><input {...F('branch_name')} className={inputCls} /></label>
              <label className={labelCls}><span className={spanCls}>Account Holder Name</span><input {...F('account_holder_name')} className={inputCls} /></label>
              <label className={labelCls}><span className={spanCls}>Account Number</span><input {...F('account_number')} className={inputCls} /></label>
              <label className={labelCls}><span className={spanCls}>IFSC Code</span><input {...F('ifsc_code')} className={inputCls} /></label>
            </div>
          </div>

          <label className={labelCls}><span className={spanCls}>Remarks</span><textarea rows={2} {...F('remarks')} className={inputCls} /></label>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : (isEdit ? 'Update Nominee' : 'Add Nominee')}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const ActionModal = ({ mode, profile, employeeProfile, nominees, familyDetails, onClose, onSubmit, loading }) => {
  const isEdit = mode === 'edit';
  const isRevise = mode === 'revise';
  const isStop = mode === 'stop';
  const isResume = mode === 'resume';
  const isMarkDeceased = mode === 'mark-deceased';

  const empPersonal = employeeProfile?.personal || {};
  const empEmployment = employeeProfile?.employment || {};
  const empBank = employeeProfile?.bank || {};

  const [designationsList, setDesignationsList] = useState([]);

  useEffect(() => {
    if (isEdit) {
      axios.get('/v1/reference-data/designations', { headers: authHeaders() })
        .then((res) => {
          if (Array.isArray(res.data)) {
            setDesignationsList(res.data);
          }
        })
        .catch((err) => {
          console.warn('Could not fetch designations list:', err);
        });
    }
  }, [isEdit]);

  const [form, setForm] = useState({
    // 1. Pen code
    pen_code: empPersonal.emp_code || profile?.employee_code || '',
    // 2. Name of the pensioner
    pensioner_name: empPersonal.full_name || profile?.employee_name || '',
    // 3. Designation
    designation: empEmployment.designation || profile?.designation || '',
    // 4. Date of Retirement
    retirement_date: profile?.retirement_date || empPersonal.retirement_date || '',
    // 5. Date of Death & Family Pension Name
    date_of_death: profile?.date_of_death || '',
    family_pension_name: profile?.family_recipient_name || profile?.family_recipient || '',
    family_pension_recipient_id: profile?.family_pension_recipient_id || '',
    // 6. Class of Pension
    class_of_pension: profile?.retirement_type || 'superannuation',
    // 7. Aadhaar card No
    aadhaar_card_no: empPersonal.aadhaar_number || '',
    // 8. PAN card No
    pan_card_no: empPersonal.pan_number || '',
    // 9. Mobile No, Mail ID & Address
    mobile_no: empPersonal.phone_number || empPersonal.mobile_number || '',
    mail_id: empPersonal.email || '',
    address: empPersonal.address_line1 || empPersonal.current_address || empPersonal.address || '',
    // 10. Bank A/C No
    bank_account_no: profile?.bank_account_number || empBank.bank_account_number || '',
    // 11. Basic
    basic: profile?.basic_pension !== undefined && profile?.basic_pension !== null ? profile.basic_pension : '',
    // 12. Dearness Relief (DR)
    dr_amount: profile?.basic_pension ? Math.round(Number(profile.basic_pension) * 0.5) : '',
    // 13. Arrears
    arrears: '0',
    // 14. Medical Allowance
    medical_allowance: profile?.medical_allowance ?? 300,
    // 15. Gross Total
    gross_total: '',
    is_gross_custom: false,
    // 16. Commutation Amount
    commutation_amount: '0',
    // 17. Recovery
    recovery: '0',
    // 18. Income Tax
    income_tax: '0',
    // 19. Net Total
    net_total: '',
    is_net_custom: false,
    // 20. Quarters
    quarters: empPersonal.has_assigned_quarter ? 'Y' : 'N',

    // Modal fields for other actions
    ppo_number: profile?.ppo_number || '',
    pension_category: profile?.pension_category || '',
    pension_start_date: profile?.pension_start_date || '',
    status: profile?.status || 'active',
    remarks: profile?.remarks || '',
    effective_date: new Date().toISOString().slice(0, 10),
    previous_amount: profile?.basic_pension || '',
    revised_amount: profile?.basic_pension || '',
    revision_reason: '',
    reason: '',
  });

  useEffect(() => {
    const empP = employeeProfile?.personal || {};
    const empE = employeeProfile?.employment || {};
    const empB = employeeProfile?.bank || {};

    setForm({
      pen_code: empP.emp_code || profile?.employee_code || '',
      pensioner_name: empP.full_name || profile?.employee_name || '',
      designation: empE.designation || profile?.designation || '',
      retirement_date: profile?.retirement_date || empP.retirement_date || '',
      date_of_death: profile?.date_of_death || '',
      family_pension_name: profile?.family_recipient_name || profile?.family_recipient || '',
      family_pension_recipient_id: profile?.family_pension_recipient_id || '',
      class_of_pension: profile?.retirement_type || 'superannuation',
      aadhaar_card_no: empP.aadhaar_number || '',
      pan_card_no: empP.pan_number || '',
      mobile_no: empP.phone_number || empP.mobile_number || '',
      mail_id: empP.email || '',
      address: empP.address_line1 || empP.current_address || empP.address || '',
      bank_account_no: profile?.bank_account_number || empB.bank_account_number || '',
      basic: profile?.basic_pension !== undefined && profile?.basic_pension !== null ? profile.basic_pension : '',
      dr_amount: profile?.basic_pension ? Math.round(Number(profile.basic_pension) * 0.5) : '',
      arrears: '0',
      medical_allowance: profile?.medical_allowance ?? 300,
      gross_total: '',
      is_gross_custom: false,
      commutation_amount: '0',
      recovery: '0',
      income_tax: '0',
      net_total: '',
      is_net_custom: false,
      quarters: empP.has_assigned_quarter ? 'Y' : 'N',

      ppo_number: profile?.ppo_number || '',
      pension_category: profile?.pension_category || '',
      pension_start_date: profile?.pension_start_date || '',
      status: profile?.status || 'active',
      remarks: profile?.remarks || '',
      effective_date: new Date().toISOString().slice(0, 10),
      previous_amount: profile?.basic_pension || '',
      revised_amount: profile?.basic_pension || '',
      revision_reason: '',
      reason: '',
    });
  }, [profile, employeeProfile, mode]);

  useEffect(() => {
    if (!isEdit) return;
    const b = Number(form.basic || 0);
    const dr = Number(form.dr_amount || 0);
    const arr = Number(form.arrears || 0);
    const med = Number(form.medical_allowance || 0);

    const calculatedGross = b + dr + arr + med;
    const currentGross = form.is_gross_custom ? Number(form.gross_total || 0) : calculatedGross;

    const comm = Number(form.commutation_amount || 0);
    const rec = Number(form.recovery || 0);
    const tax = Number(form.income_tax || 0);
    const calculatedNet = currentGross - comm - rec - tax;

    setForm(prev => ({
      ...prev,
      gross_total: prev.is_gross_custom ? prev.gross_total : String(calculatedGross),
      net_total: prev.is_net_custom ? prev.net_total : String(calculatedNet < 0 ? 0 : calculatedNet)
    }));
  }, [form.basic, form.dr_amount, form.arrears, form.medical_allowance, form.commutation_amount, form.recovery, form.income_tax, form.is_gross_custom, form.is_net_custom, isEdit]);

  const submit = () => {
    if (isEdit) {
      onSubmit({
        pen_code: form.pen_code,
        pensioner_name: form.pensioner_name,
        designation: form.designation,
        retirement_date: form.retirement_date,
        date_of_death: form.date_of_death,
        family_pension_name: form.family_pension_name,
        family_pension_recipient_id: form.family_pension_recipient_id,
        class_of_pension: form.class_of_pension,
        aadhaar_card_no: form.aadhaar_card_no,
        pan_card_no: form.pan_card_no,
        mobile_no: form.mobile_no,
        mail_id: form.mail_id,
        address: form.address,
        bank_account_no: form.bank_account_no,
        basic: form.basic,
        dr_amount: form.dr_amount,
        arrears: form.arrears,
        medical_allowance: form.medical_allowance,
        gross_total: form.gross_total,
        commutation_amount: form.commutation_amount,
        recovery: form.recovery,
        income_tax: form.income_tax,
        net_total: form.net_total,
        quarters: form.quarters,
      });
      return;
    }

    if (isRevise) {
      onSubmit({
        effective_date: form.effective_date,
        previous_amount: Number(form.previous_amount || 0),
        revised_amount: Number(form.revised_amount || 0),
        revision_reason: form.revision_reason,
        remarks: form.remarks || null,
      });
      return;
    }

    if (isMarkDeceased) {
      if (!form.date_of_death || !form.family_pension_recipient_id) {
        alert('Date of Death and Family Pension Recipient Nominee are mandatory.');
        return;
      }
      onSubmit({
        date_of_death: form.date_of_death,
        family_pension_recipient_id: Number(form.family_pension_recipient_id)
      });
      return;
    }

    onSubmit({
      effective_date: form.effective_date || null,
      reason: form.reason || null,
      remarks: form.remarks || null,
    });
  };

  const title = isEdit ? 'Edit Pensioner Profile' : isRevise ? 'Revise Pension Amount' : isStop ? 'Stop Pension' : isMarkDeceased ? 'Mark Pensioner Deceased' : 'Resume Pension';
  const description = isEdit
    ? 'Modify pensioner employee profile details and pension payroll components.'
    : isRevise
      ? 'Revise the current pension amount and preserve the revision trail.'
      : isStop
        ? 'Temporarily stop pension disbursement while preserving the profile history.'
        : isMarkDeceased
          ? 'Record the pensioner\'s date of death and select the family pension recipient nominee.'
          : 'Resume pension disbursement for this pensioner profile.';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.98 }}
        className={`w-full ${isEdit ? 'max-w-4xl' : 'max-w-2xl'} overflow-hidden rounded-2xl bg-white shadow-2xl`}
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4 bg-slate-50/50">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500">{description}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto p-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {isEdit && (
              <div className="space-y-6 md:col-span-2">
                {/* Section 1: Basic Pensioner Details */}
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 border-b pb-1">1. Pensioner Identity & Retirement Details</h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">1) Pen Code (Pensioner / Emp Code)</span>
                      <input
                        value={form.pen_code}
                        onChange={(e) => setForm((prev) => ({ ...prev, pen_code: e.target.value }))}
                        placeholder="e.g. EMP001"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">2) Name of the Pensioner</span>
                      <input
                        value={form.pensioner_name}
                        onChange={(e) => setForm((prev) => ({ ...prev, pensioner_name: e.target.value }))}
                        placeholder="Pensioner Full Name"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">3) Designation</span>
                      <div className="relative">
                        <select
                          value={form.designation || ''}
                          onChange={(e) => setForm((prev) => ({ ...prev, designation: e.target.value }))}
                          className="w-full appearance-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">-- Select Designation --</option>
                          {designationsList.map((d) => (
                            <option key={d.id || d.title} value={d.title}>
                              {d.title} {d.code ? `(${d.code})` : ''}
                            </option>
                          ))}
                          {form.designation && !designationsList.some(d => d.title === form.designation) && (
                            <option value={form.designation}>{form.designation}</option>
                          )}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      </div>
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">4) Date of Retirement</span>
                      <input
                        type="date"
                        value={form.retirement_date || ''}
                        onChange={(e) => setForm((prev) => ({ ...prev, retirement_date: e.target.value }))}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">5) Class of Pension</span>
                      <div className="relative">
                        <select
                          value={form.class_of_pension || 'superannuation'}
                          onChange={(e) => setForm((prev) => ({ ...prev, class_of_pension: e.target.value }))}
                          className="w-full appearance-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="superannuation">1) Superannuation</option>
                          <option value="voluntary_retirement">2) VRS (Voluntarily)</option>
                          <option value="medical_retirement">3) Medical Unfit</option>
                          <option value="death_in_service">4) Expired / Death</option>
                          <option value="compulsory_retirement">5) Compulsory Retirement</option>
                          <option value="absconding">6) Absconded</option>
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      </div>
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">5a) Date of Death (if applicable)</span>
                      <input
                        type="date"
                        value={form.date_of_death || ''}
                        onChange={(e) => setForm((prev) => ({ ...prev, date_of_death: e.target.value }))}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                    <label className="space-y-1 text-sm md:col-span-2">
                      <span className="font-medium text-slate-700">5b) Family Pension Name (Recipient / Nominee)</span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="relative">
                          <select
                            value={form.family_pension_recipient_id || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              const selectedNominee = nominees?.find(n => String(n.id) === val);
                              const selectedFamily = familyDetails?.find(f => String(f.id || f.memberName || f.name) === val);
                              const recipientName = selectedNominee ? selectedNominee.nominee_name : (selectedFamily ? (selectedFamily.memberName || selectedFamily.name) : '');
                              setForm((prev) => ({
                                ...prev,
                                family_pension_recipient_id: val,
                                family_pension_name: recipientName || prev.family_pension_name
                              }));
                            }}
                            className="w-full appearance-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">-- Select Nominee / Family Member --</option>
                            {nominees && nominees.map((nom) => (
                              <option key={`nom-${nom.id}`} value={nom.id}>{nom.nominee_name} ({nom.relationship || 'Nominee'})</option>
                            ))}
                            {familyDetails && familyDetails.map((fam, idx) => (
                              <option key={`fam-${idx}`} value={fam.id || fam.memberName || fam.name}>
                                {fam.memberName || fam.name} ({fam.relation || fam.relationship || 'Family Member'})
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        </div>
                        <input
                          value={form.family_pension_name || ''}
                          onChange={(e) => setForm((prev) => ({ ...prev, family_pension_name: e.target.value }))}
                          placeholder="Enter / Edit Family Pensioner Name"
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </label>
                  </div>
                </div>

                {/* Section 2: Personal & Contact Information */}
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 border-b pb-1">2. Contact, Identity & Accommodation</h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">6) Aadhaar Card No</span>
                      <input
                        value={form.aadhaar_card_no}
                        onChange={(e) => setForm((prev) => ({ ...prev, aadhaar_card_no: e.target.value }))}
                        placeholder="12-digit Aadhaar Number"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">7) PAN Card No</span>
                      <input
                        value={form.pan_card_no}
                        onChange={(e) => setForm((prev) => ({ ...prev, pan_card_no: e.target.value }))}
                        placeholder="10-digit PAN Number"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono uppercase"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">8a) Mobile No</span>
                      <input
                        value={form.mobile_no}
                        onChange={(e) => setForm((prev) => ({ ...prev, mobile_no: e.target.value }))}
                        placeholder="Mobile Number"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">8b) Mail ID (Email)</span>
                      <input
                        type="email"
                        value={form.mail_id}
                        onChange={(e) => setForm((prev) => ({ ...prev, mail_id: e.target.value }))}
                        placeholder="Email Address"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                    <label className="space-y-1 text-sm md:col-span-2">
                      <span className="font-medium text-slate-700">8c) Address</span>
                      <textarea
                        rows={2}
                        value={form.address}
                        onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                        placeholder="Full Residential Address"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                    <label className="space-y-1 text-sm md:col-span-2">
                      <span className="font-medium text-slate-700">19) Quarters (Government Assigned Quarter)</span>
                      <div className="flex items-center gap-6 mt-1">
                        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-700">
                          <input
                            type="radio"
                            name="quarters"
                            value="Y"
                            checked={form.quarters === 'Y'}
                            onChange={(e) => setForm((prev) => ({ ...prev, quarters: e.target.value }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          Yes (Y)
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-700">
                          <input
                            type="radio"
                            name="quarters"
                            value="N"
                            checked={form.quarters === 'N'}
                            onChange={(e) => setForm((prev) => ({ ...prev, quarters: e.target.value }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          No (N)
                        </label>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Section 3: Bank Account */}
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 border-b pb-1">3. Bank Account Details</h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">Bank Name</span>
                      <input
                        value="State Bank of India (SBI)"
                        readOnly
                        disabled
                        className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 font-semibold text-slate-700 cursor-not-allowed text-sm"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">9) Bank A/C No</span>
                      <input
                        value={form.bank_account_no}
                        onChange={(e) => setForm((prev) => ({ ...prev, bank_account_no: e.target.value }))}
                        placeholder="Bank Account Number"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </label>
                  </div>
                </div>

                {/* Section 4: Pension Salary & Allowances Breakdown */}
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 border-b pb-1">4. Pension Breakdown & Calculations</h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">10) Basic Pension</span>
                      <input
                        type="number"
                        step="0.01"
                        value={form.basic}
                        onChange={(e) => setForm((prev) => ({ ...prev, basic: e.target.value }))}
                        placeholder="e.g. 25000"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">11) Dearness Relief - (DR) (+)</span>
                      <input
                        type="number"
                        step="0.01"
                        value={form.dr_amount}
                        onChange={(e) => setForm((prev) => ({ ...prev, dr_amount: e.target.value }))}
                        placeholder="DR Amount"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">12) Arrears (+)</span>
                      <input
                        type="number"
                        step="0.01"
                        value={form.arrears}
                        onChange={(e) => setForm((prev) => ({ ...prev, arrears: e.target.value }))}
                        placeholder="0.00"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">13) Medical Allowance (+)</span>
                      <input
                        type="number"
                        step="0.01"
                        value={form.medical_allowance}
                        onChange={(e) => setForm((prev) => ({ ...prev, medical_allowance: e.target.value }))}
                        placeholder="e.g. 300"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </label>
                    <label className="space-y-1 text-sm md:col-span-2 bg-blue-50/50 p-2.5 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-blue-900">14) Gross Total (+)</span>
                        <span className="text-xs text-blue-600 font-normal">(Basic + DR + Arrears + Medical Allowance)</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        value={form.gross_total}
                        onChange={(e) => setForm((prev) => ({ ...prev, gross_total: e.target.value, is_gross_custom: true }))}
                        className="w-full rounded-lg border border-blue-300 px-3 py-2 text-base font-bold text-blue-900 font-mono bg-white focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">15) Commutation Amount (-)</span>
                      <input
                        type="number"
                        step="0.01"
                        value={form.commutation_amount}
                        onChange={(e) => setForm((prev) => ({ ...prev, commutation_amount: e.target.value }))}
                        placeholder="0.00"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">16) Recovery (-)</span>
                      <input
                        type="number"
                        step="0.01"
                        value={form.recovery}
                        onChange={(e) => setForm((prev) => ({ ...prev, recovery: e.target.value }))}
                        placeholder="0.00"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </label>
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-slate-700">17) Income Tax (-)</span>
                      <input
                        type="number"
                        step="0.01"
                        value={form.income_tax}
                        onChange={(e) => setForm((prev) => ({ ...prev, income_tax: e.target.value }))}
                        placeholder="0.00"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </label>
                    <label className="space-y-1 text-sm md:col-span-2 bg-emerald-50/50 p-2.5 rounded-lg border border-emerald-100">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-emerald-900">18) Net Total</span>
                        <span className="text-xs text-emerald-600 font-normal">(Gross Total - Commutation - Recovery - Income Tax)</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        value={form.net_total}
                        onChange={(e) => setForm((prev) => ({ ...prev, net_total: e.target.value, is_net_custom: true }))}
                        className="w-full rounded-lg border border-emerald-300 px-3 py-2 text-base font-bold text-emerald-900 font-mono bg-white focus:ring-2 focus:ring-emerald-500"
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}

            {isRevise && (
              <>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-slate-700">Effective Date</span>
                  <input type="date" value={form.effective_date || ''} onChange={(e) => setForm((prev) => ({ ...prev, effective_date: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-slate-700">Previous Amount</span>
                  <input type="number" min="0" step="0.01" value={form.previous_amount || ''} onChange={(e) => setForm((prev) => ({ ...prev, previous_amount: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-slate-700">Revised Amount</span>
                  <input type="number" min="0" step="0.01" value={form.revised_amount || ''} onChange={(e) => setForm((prev) => ({ ...prev, revised_amount: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-slate-700">Revision Reason</span>
                  <input value={form.revision_reason || ''} onChange={(e) => setForm((prev) => ({ ...prev, revision_reason: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                </label>
                <label className="space-y-1 text-sm md:col-span-2">
                  <span className="font-medium text-slate-700">Remarks</span>
                  <textarea rows={3} value={form.remarks || ''} onChange={(e) => setForm((prev) => ({ ...prev, remarks: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                </label>
              </>
            )}

            {(isStop || isResume) && (
              <>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-slate-700">Effective Date</span>
                  <input type="date" value={form.effective_date || ''} onChange={(e) => setForm((prev) => ({ ...prev, effective_date: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                </label>
                {isStop ? (
                  <label className="space-y-1 text-sm">
                    <span className="font-medium text-slate-700">Stop Reason</span>
                    <div className="relative">
                      <select value={form.reason || ''} onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))} className="w-full appearance-none rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500">
                        <option value="">Select a reason</option>
                        <option value="death">Death</option>
                        <option value="court_order">Court Order</option>
                        <option value="administrative_hold">Administrative Hold</option>
                        <option value="duplicate">Duplicate</option>
                        <option value="other">Other</option>
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    </div>
                  </label>
                ) : null}
                <label className="space-y-1 text-sm md:col-span-2">
                  <span className="font-medium text-slate-700">Remarks</span>
                  <textarea rows={3} value={form.remarks || ''} onChange={(e) => setForm((prev) => ({ ...prev, remarks: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500" />
                </label>
              </>
            )}

            {isMarkDeceased && (
              <>
                <label className="space-y-1 text-sm md:col-span-2">
                  <span className="font-medium text-slate-700">Date of Death</span>
                  <input type="date" value={form.date_of_death || ''} onChange={(e) => setForm((prev) => ({ ...prev, date_of_death: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500" required />
                </label>
                <label className="space-y-1 text-sm md:col-span-2">
                  <span className="font-medium text-slate-700">Family Pension Recipient</span>
                  <div className="relative">
                    <select value={form.family_pension_recipient_id || ''} onChange={(e) => setForm((prev) => ({ ...prev, family_pension_recipient_id: e.target.value }))} className="w-full appearance-none rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500" required>
                      <option value="">Select an Approved Nominee</option>
                      {nominees && nominees.filter(n => n.status === 'approved').map((nom) => (
                        <option key={nom.id} value={nom.id}>{nom.nominee_name} ({nom.relationship})</option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  </div>
                </label>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={submit} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const PensionerProfilePage = () => {
  const { hasPermission } = useAuth();
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [history, setHistory] = useState([]);
  const [nominees, setNominees] = useState([]);
  const [qualifyingService, setQualifyingService] = useState(null);
  const [familyDetails, setFamilyDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalMode, setModalMode] = useState('');
  const [nomineeModal, setNomineeModal] = useState(null); // null = closed | 'add' | {nominee object} for edit
  const [quarterValue, setQuarterValue] = useState(false);
  const [quarterSaving, setQuarterSaving] = useState(false);
  const [quarterError, setQuarterError] = useState('');
  const [quarterSuccess, setQuarterSuccess] = useState('');

  const activeTab = searchParams.get('tab') || 'overview';

  const auth = authHeaders();

  useEffect(() => {
    if (employeeProfile?.personal) {
      setQuarterValue(!!employeeProfile.personal.has_assigned_quarter);
    }
  }, [employeeProfile]);

  const handleQuarterUpdate = async () => {
    if (!profile?.employee_id) return;
    setQuarterSaving(true);
    setQuarterError('');
    setQuarterSuccess('');
    try {
      await axios.put(`/v1/employee-master/${profile.employee_id}`, {
        has_assigned_quarter: quarterValue,
      }, { headers: auth });
      setQuarterSuccess('Government Quarter status updated successfully.');
      load();
    } catch (error) {
      setQuarterError(error?.response?.data?.detail || 'Failed to update Government Quarter status.');
    } finally {
      setQuarterSaving(false);
    }
  };

  const getNextBirthday = (dobString) => {
    if (!dobString) return '-';
    try {
      const parts = dobString.split('-');
      if (parts.length < 3) return '-';
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const today = new Date();
      let nextBirthday = new Date(today.getFullYear(), month, day);
      const todayZero = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      if (nextBirthday < todayZero) {
        nextBirthday.setFullYear(today.getFullYear() + 1);
      }
      const yyyy = nextBirthday.getFullYear();
      const mm = String(nextBirthday.getMonth() + 1).padStart(2, '0');
      const dd = String(nextBirthday.getDate()).padStart(2, '0');
      return formatDate(`${yyyy}-${mm}-${dd}`);
    } catch {
      return '-';
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const profileRes = await axios.get(`/v1/pensioners/by-employee/${employeeId}`, { headers: auth });
      const pensioner = profileRes.data;
      setProfile(pensioner);

      const [employeeRes, docsRes, historyRes, nomineesRes, familyRes, qsRes] = await Promise.all([
        axios.get(`/v1/employee-master/${pensioner.employee_id}/profile-360`, { headers: auth }).catch(() => null),
        axios.get(`/v1/employee-master/${pensioner.employee_id}/documents`, { headers: auth }).catch(() => null),
        axios.get(`/v1/pensioners/${pensioner.id}/history`, { headers: auth }).catch(() => null),
        axios.get(`/v1/pensioners/${pensioner.id}/nominees`, { headers: auth }).catch(() => null),
        axios.get(`/v1/employees/${pensioner.employee_id}/joining-form-submissions`, { headers: auth }).catch(() => null),
        axios.get(`/v1/employee-master/${pensioner.employee_id}/qualifying-service`, { headers: auth }).catch(() => null),
      ]);

      setEmployeeProfile(employeeRes?.data || null);
      setDocuments(Array.isArray(docsRes?.data) ? docsRes.data : []);
      setHistory(Array.isArray(historyRes?.data) ? historyRes.data : []);
      setNominees(Array.isArray(nomineesRes?.data) ? nomineesRes.data : []);
      setQualifyingService(qsRes?.data || null);

      if (familyRes && Array.isArray(familyRes.data)) {
        const approvedFamily = familyRes.data.find(x => x.form_code === 'FAMILY' && x.status === 'APPROVED');
        if (approvedFamily && approvedFamily.form_data && Array.isArray(approvedFamily.form_data.familyMembers)) {
          setFamilyDetails(approvedFamily.form_data.familyMembers);
        } else {
          const submittedFamily = familyRes.data.find(x => x.form_code === 'FAMILY');
          if (submittedFamily && submittedFamily.form_data && Array.isArray(submittedFamily.form_data.familyMembers)) {
            setFamilyDetails(submittedFamily.form_data.familyMembers);
          }
        }
      }
    } catch (err) {
      setError(formatApiError(err, 'Unable to load pensioner profile'));
      setProfile(null);
      setEmployeeProfile(null);
      setDocuments([]);
      setHistory([]);
      setNominees([]);
      setQualifyingService(null);
      setFamilyDetails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  useEffect(() => {
    const action = searchParams.get('action');
    if (['edit', 'revise', 'stop', 'resume'].includes(action)) {
      setModalMode(action);
    }
  }, [searchParams]);

  const employeeInfo = employeeProfile?.personal || {};
  const employmentInfo = employeeProfile?.employment || {};
  const pensionSummary = profile || {};

  const exportProfile = () => {
    if (!profile) return;
    const rows = [
      ['Employee', employeeInfo.full_name || profile.employee_name || ''],
      ['Employee Code', employeeInfo.emp_code || profile.employee_code || ''],
      ['Department', employmentInfo.department || profile.department || ''],
      ['Designation', employmentInfo.designation || profile.designation || ''],
      ['PPO Number', profile.ppo_number || ''],
      ['Retirement Date', profile.retirement_date || ''],
      ['Retirement Type', profile.retirement_type || ''],
      ['Pension Category', profile.pension_category || ''],
      ['Pension Start Date', profile.pension_start_date || ''],
      ['Basic Pension', profile.basic_pension || ''],
      ['Status', profile.status || ''],
    ];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pension_profile_${profile.employee_code || profile.employee_id}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleNomineeAction = async (nomineeId, action) => {
    if (!profile) return;
    try {
      if (action === 'approve') {
        await axios.post(`/v1/pensioners/nominees/${nomineeId}/approve`, {}, { headers: auth });
      } else {
        const reason = window.prompt('Enter rejection reason', '');
        if (!reason) return;
        await axios.post(`/v1/pensioners/nominees/${nomineeId}/reject?reason=${encodeURIComponent(reason)}`, {}, { headers: auth });
      }
      await load();
    } catch (err) {
      setError(formatApiError(err, 'Unable to update nominee'));
    }
  };

  const runAction = async (payload) => {
    if (!profile) return;
    setSaving(true);
    setError('');
    try {
      if (modalMode === 'edit') {
        // 1. Update Pensioner Profile
        const recipientId = payload.family_pension_recipient_id ? Number(payload.family_pension_recipient_id) : null;
        await axios.put(`/v1/pensioners/${profile.id}`, {
          retirement_date: payload.retirement_date || null,
          retirement_type: payload.class_of_pension || null,
          basic_pension: payload.basic !== '' && payload.basic !== null ? Number(payload.basic) : null,
          bank_name: 'State Bank of India',
          bank_account_number: payload.bank_account_no || null,
          medical_allowance: payload.medical_allowance !== '' && payload.medical_allowance !== null ? Number(payload.medical_allowance) : 0,
          date_of_death: payload.date_of_death || null,
          family_pension_recipient_id: isNaN(recipientId) ? null : recipientId,
          family_recipient_name: payload.family_pension_name || null,
          is_deceased: Boolean(payload.date_of_death),
        }, { headers: auth });

        // 2. Update Employee Master Details
        if (profile.employee_id) {
          await axios.put(`/v1/employee-master/${profile.employee_id}`, {
            emp_code: payload.pen_code || undefined,
            full_name: payload.pensioner_name || undefined,
            emp_desig: payload.designation || undefined,
            aadhaar_number: payload.aadhaar_card_no || undefined,
            pan_number: payload.pan_card_no || undefined,
            phone_number: payload.mobile_no || undefined,
            email: payload.mail_id || undefined,
            current_address: payload.address || undefined,
            bank_account_number: payload.bank_account_no || undefined,
            has_assigned_quarter: payload.quarters === 'Y',
          }, { headers: auth }).catch(err => {
            console.warn('Employee master update notice:', err);
          });
        }
      } else if (modalMode === 'revise') {
        await axios.post(`/v1/pensioners/${profile.id}/revise`, payload, { headers: auth });
      } else if (modalMode === 'stop') {
        await axios.post(`/v1/pensioners/${profile.id}/stop`, payload, { headers: auth });
      } else if (modalMode === 'resume') {
        await axios.post(`/v1/pensioners/${profile.id}/resume`, payload, { headers: auth });
      } else if (modalMode === 'mark-deceased') {
        await axios.post(`/v1/pensioners/${profile.id}/mark-deceased`, payload, { headers: auth });
      }
      await load();
      setModalMode('');
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    } catch (err) {
      setError(formatApiError(err, 'Unable to save pension changes'));
    } finally {
      setSaving(false);
    }
  };

  const setTab = (tab) => {
    searchParams.set('tab', tab);
    setSearchParams(searchParams);
  };

  const openAction = (action) => {
    searchParams.set('action', action);
    setSearchParams(searchParams, { replace: true });
    setModalMode(action);
  };

  const currentStatus = (profile?.status || 'active').toLowerCase();
  const statusLabel = profile?.is_deceased ? 'DECEASED' : (PENSION_STATUS_LABELS[currentStatus] || currentStatus.toUpperCase());
  const canResume = ['stopped'].includes(currentStatus);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-slate-600">
          <RefreshCw className="h-5 w-5 animate-spin" />
          Loading pensioner profile...
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader title="Pension Profile" subtitle="Pension management" icon={Shield} showNotification={false} showSettings={false}>
          <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        </DashboardHeader>
        <main className="mx-auto max-w-5xl px-4 py-8">
          <EmptyState icon={Shield} title="Pension profile not available" description={error || 'No pensioner profile was found for this employee.'} size="large" />
        </main>
      </div>
    );
  }

  const employeeName = employeeInfo.full_name || profile.employee_name || 'Pensioner';
  const employeeCode = employeeInfo.emp_code || profile.employee_code || '';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <DashboardHeader title="Pension Profile" subtitle={`${employeeName}${employeeCode ? ` · ${employeeCode}` : ''}`} icon={Shield} showNotification={false} showSettings={false}>
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <button type="button" onClick={load} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
        <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
          <Printer className="h-4 w-4" />
          Print
        </button>
      </DashboardHeader>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error ? (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        ) : null}

        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
          <motion.section variants={itemVariants} className="grid grid-cols-1 gap-4 xl:grid-cols-4">
            <div className="xl:col-span-2 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-100 text-blue-600">
                    <User className="h-8 w-8" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900">{employeeName}</h1>
                    <p className="text-sm text-slate-500">{employeeCode}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">Pensioner</span>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{statusLabel}</span>
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">{profile.pension_category || 'Pension'}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                  <button type="button" onClick={() => setTab('nominee')} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                    <HeartHandshake className="h-4 w-4" />
                    View Nominee
                  </button>
                  <button type="button" onClick={() => setTab('documents')} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                    <FileText className="h-4 w-4" />
                    View Documents
                  </button>
                  <button type="button" onClick={() => openAction('edit')} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                    <PencilLine className="h-4 w-4" />
                    Edit Pension Details
                  </button>
                  <button type="button" onClick={() => openAction('revise')} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700">
                    <BadgeInfo className="h-4 w-4" />
                    Revise Pension Amount
                  </button>
                  {canResume ? (
                    <button type="button" onClick={() => openAction('resume')} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100">
                      <RefreshCw className="h-4 w-4" />
                      Resume Pension
                    </button>
                  ) : (
                    <button type="button" onClick={() => openAction('stop')} className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100">
                      <StopCircle className="h-4 w-4" />
                      Stop Pension
                    </button>
                  )}
                  {!profile.is_deceased && !profile.date_of_death && profile.retirement_type !== 'death_in_service' && (
                    <button type="button" onClick={() => openAction('mark-deceased')} className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100">
                      <XCircle className="h-4 w-4" />
                      Mark Deceased
                    </button>
                  )}
                </div>
              </div>
            </div>

            <StatCard title="Basic Pension" value={Number(profile.basic_pension || 0)} icon={Banknote} color="bg-blue-600" subtitle="Current monthly amount" />
            <StatCard title="Pension Start" value={profile.pension_start_date ? formatDate(profile.pension_start_date) : '-'} icon={History} color="bg-slate-700" useCountUp={false} />
            <StatCard title="Retirement Date" value={profile.retirement_date ? formatDate(profile.retirement_date) : '-'} icon={Shield} color="bg-emerald-600" useCountUp={false} />
          </motion.section>

          <div className="flex gap-2 overflow-x-auto rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
            {TAB_IDS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setTab(tab)}
                className={`rounded-lg px-4 py-2 text-sm font-medium capitalize ${activeTab === tab ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                {tab.replace(/_/g, ' ')}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <motion.section variants={itemVariants} className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <SectionCard title="Employee Information" icon={User} accent="bg-slate-700">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Info label="Employee Name" value={employeeName} />
                  <Info label="Employee Code" value={employeeCode} />
                  <Info label="Department" value={employmentInfo.department || profile.department || '-'} />
                  <Info label="Designation" value={employmentInfo.designation || profile.designation || '-'} />
                  <Info label="Role" value={employmentInfo.role || profile.employee_role_name || '-'} />
                  <Info label="Employment Status" value={employeeProfile?.employment?.employment_status || profile.employee_status || '-'} />
                </div>
              </SectionCard>

              <SectionCard title="Current Pension" icon={Banknote} accent="bg-blue-600">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Info label="PPO Number" value={profile.ppo_number || '-'} />
                  <Info label="Pension Status" value={statusLabel} />
                  <Info label="Pension Category" value={profile.pension_category || '-'} />
                  <Info label="Retirement Type" value={profile.retirement_type || '-'} />
                  <Info label="Basic Pension" value={formatCurrency(profile.basic_pension || 0)} />
                  <Info label="Last Payroll Month" value={profile.last_payroll_month || '-'} />
                  <Info label="Last Payroll Basic" value={profile.last_payroll_basic ? formatCurrency(profile.last_payroll_basic) : '-'} />
                  <Info label="Pension Period" value={`${profile.pension_start_date ? formatDate(profile.pension_start_date) : '-'} to ${profile.pension_applicable_till ? formatDate(profile.pension_applicable_till) : '-'}`} />
                  <Info label="Life Status" value={profile.is_deceased ? `Deceased (Died: ${formatDate(profile.date_of_death)})` : 'Alive'} />
                  {profile.is_deceased && (
                    <>
                      <Info label="Family Recipient" value={profile.family_recipient_name || '-'} />
                      <Info label="Recipient Relation" value={profile.family_recipient_relation || '-'} />
                    </>
                  )}
                  <Info label="Revision Count" value={history.filter((item) => item.action === 'revise_pension').length} />
                </div>
              </SectionCard>

              <div className="xl:col-span-2">
                <SectionCard title="Government Quarter Assignment" icon={Home} accent="bg-purple-600">
                  <div className="space-y-4">
                    {quarterError && (
                      <div className="rounded-lg bg-red-50 p-3 text-xs text-red-700 border border-red-200">
                        {quarterError}
                      </div>
                    )}
                    {quarterSuccess && (
                      <div className="rounded-lg bg-emerald-50 p-3 text-xs text-emerald-700 border border-emerald-200">
                        {quarterSuccess}
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
                        Has Assigned Government Quarter?
                      </label>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-700">
                          <input
                            type="radio"
                            name="pensioner_has_assigned_quarter"
                            value="true"
                            checked={quarterValue === true}
                            onChange={() => setQuarterValue(true)}
                            className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-slate-300"
                          />
                          Yes
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-700">
                          <input
                            type="radio"
                            name="pensioner_has_assigned_quarter"
                            value="false"
                            checked={quarterValue === false}
                            onChange={() => setQuarterValue(false)}
                            className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-slate-300"
                          />
                          No
                        </label>
                      </div>
                    </div>

                    <p className="text-xs text-slate-500 italic">
                      Note: Pensioners with an assigned government quarter are not eligible for DA or Medical Allowance in Pension Payroll.
                    </p>

                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div className="text-xs text-slate-600">
                        <span className="font-medium">Current Status:</span>{' '}
                        {employeeProfile?.personal?.has_assigned_quarter === true ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                            Yes (Assigned)
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                            No (Not Assigned)
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleQuarterUpdate}
                        disabled={quarterSaving || quarterValue === !!employeeProfile?.personal?.has_assigned_quarter}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                      >
                        {quarterSaving ? 'Updating...' : 'Update Status'}
                      </button>
                    </div>
                  </div>
                </SectionCard>
              </div>
            </motion.section>
          )}

          {activeTab === 'employee' && (
            <motion.section variants={itemVariants} className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <SectionCard title="Employee Master" icon={User} accent="bg-slate-700">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Info label="Department" value={employmentInfo.department || profile.department || '-'} />
                  <Info label="Designation" value={employmentInfo.designation || profile.designation || '-'} />
                  <Info label="Join Date / Date of Appointment (DOA)" value={employmentInfo.join_date ? formatDate(employmentInfo.join_date) : '-'} />
                  {/* <Info label="Branch" value={employmentInfo.hospital_branch || '-'} /> 
                  <Info label="Work Location" value={employmentInfo.work_location || '-'} />*/}
                  <Info label="Employment Status" value={employmentInfo.employment_status || profile.employee_status || '-'} />
                </div>
              </SectionCard>
              <SectionCard title="Employee Identity" icon={Shield} accent="bg-emerald-600">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {/* <Info label="Date of Birth (DOB)" value={employeeInfo.date_of_birth ? formatDate(employeeInfo.date_of_birth) : '-'} /> */}
                  <Info label="Gender" value={employeeInfo.gender || employeeInfo.sex || '-'} />
                  <Info label="Mobile" value={employeeInfo.phone || '-'} />
                  <Info label="Email" value={employeeInfo.email || employeeInfo.official_email || '-'} />
                  <Info label="PAN" value={employeeInfo.pan_number || '-'} />
                  <Info label="Aadhaar" value={employeeInfo.aadhaar_number || '-'} />
                </div>
              </SectionCard>
              <SectionCard title="Address Details" icon={MapPin} accent="bg-blue-600">
                <div className="space-y-4">
                  <Info label="Current Address" value={employeeInfo.current_address || '-'} />
                  <Info label="Permanent Address" value={employeeInfo.permanent_address || '-'} />
                </div>
              </SectionCard>
              <SectionCard title="Important Dates" icon={Calendar} accent="bg-emerald-600">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Info label="Date of Birth (DOB)" value={employeeInfo.date_of_birth ? formatDate(employeeInfo.date_of_birth) : '-'} />
                  <Info label="Date of Appointment (DOA)" value={employmentInfo.join_date ? formatDate(employmentInfo.join_date) : '-'} />
                  <Info label="Date of Retirement (DOR)" value={employmentInfo.retirement_date ? formatDate(employmentInfo.retirement_date) : '-'} />
                  <Info label="Next Birthday" value={getNextBirthday(employeeInfo.date_of_birth)} />
                </div>
              </SectionCard>
              {qualifyingService && (
                <div className="xl:col-span-2">
                  <SectionCard title="Qualifying Service Summary" icon={History} accent="bg-slate-700">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Info label="Total Service" value={`${qualifyingService.total_service?.years || 0} Years, ${qualifyingService.total_service?.months || 0} Months, ${qualifyingService.total_service?.days || 0} Days`} />
                      <Info label="Extraordinary Leave (EOL)" value={`${qualifyingService.eol?.total_days || 0} Days (${qualifyingService.eol?.years || 0} Y, ${qualifyingService.eol?.months || 0} M, ${qualifyingService.eol?.days || 0} D)`} />
                      <Info label="Total Net Service" value={`${qualifyingService.total_net_service?.years || 0} Years, ${qualifyingService.total_net_service?.months || 0} Months, ${qualifyingService.total_net_service?.days || 0} Days (${qualifyingService.total_net_service?.decimal_years?.toFixed(2) || 0} Years)`} />
                      <Info label="Net Qualifying Service (Max 33 Y)" value={`${qualifyingService.net_qualifying_service?.toFixed(2) || 0} Years`} />
                    </div>
                  </SectionCard>
                </div>
              )}
            </motion.section>
          )}

          {activeTab === 'pension' && (
            <motion.section variants={itemVariants} className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <SectionCard title="Pension Information" icon={Banknote} accent="bg-blue-600">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Info label="PPO Number" value={profile.ppo_number || '-'} />
                  <Info label="Retirement Date" value={profile.retirement_date ? formatDate(profile.retirement_date) : '-'} />
                  <Info label="Retirement Type" value={profile.retirement_type || '-'} />
                  <Info label="Pension Category" value={profile.pension_category || '-'} />
                  <Info label="Pension Start Date" value={profile.pension_start_date ? formatDate(profile.pension_start_date) : '-'} />
                  <Info label="Pension Status" value={statusLabel} />
                  {profile.is_deceased && (
                    <Info label="Date of Death / Expired (DOE)" value={profile.date_of_death ? formatDate(profile.date_of_death) : '-'} />
                  )}
                </div>
              </SectionCard>
              <SectionCard title="Current Pension" icon={BadgeInfo} accent="bg-slate-700">
                <div className="space-y-4">
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Monthly Pension</p>
                    <p className="mt-1 text-3xl font-bold text-slate-900">{formatCurrency(profile.basic_pension || 0)}</p>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Info label="Revision Count" value={history.filter((item) => item.action === 'revise_pension').length} />
                    <Info label="Last Update" value={history[0]?.created_at ? formatDate(history[0].created_at) : '-'} />
                  </div>
                </div>
              </SectionCard>
            </motion.section>
          )}

          {activeTab === 'bank' && (
            <SectionCard title="Bank Details" icon={Banknote} accent="bg-emerald-600">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Info label="Bank Name" value={profile.bank_name || '-'} />
                <Info label="Account Number" value={profile.bank_account_number || '-'} />
                <Info label="IFSC" value={profile.bank_ifsc_code || '-'} />
                <Info label="Branch" value={profile.bank_branch || '-'} />
              </div>
            </SectionCard>
          )}

          {activeTab === 'nominee' && (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <SectionCard title="Family & Relations" icon={User} accent="bg-blue-600">
                <div className="space-y-6">
                  {/* Core Family Info */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 bg-slate-50 rounded-xl p-4">
                    <Info label="Father's Name" value={employeeInfo.father_name || '-'} />
                    <Info label="Mother's Name" value={employeeInfo.mother_name || '-'} />
                    <Info label="Marital Status" value={employeeInfo.marital_status || '-'} />
                    <Info label="Emergency Contact" value={employeeInfo.emergency_contact_name ? `${employeeInfo.emergency_contact_name} (${employeeInfo.emergency_contact_number || '-'})` : '-'} />
                  </div>

                  {/* Family Members List */}
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Family Members (From Declaration)</h4>
                    {familyDetails.length === 0 ? (
                      <p className="text-sm text-slate-500 italic bg-white border border-dashed border-slate-200 rounded-xl p-4 text-center">
                        No additional family member declarations found.
                      </p>
                    ) : (
                      <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50 text-slate-700">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-semibold">Name</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold">Relationship</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold">DOB</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold">Remarks</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {familyDetails.map((member, idx) => (
                              <tr key={idx} className="hover:bg-slate-50">
                                <td className="px-4 py-3 font-medium text-gray-900">{member.name || '-'}</td>
                                <td className="px-4 py-3 text-gray-700">{member.relationship || '-'}</td>
                                <td className="px-4 py-3 text-gray-700">{member.dateOfBirth ? formatDate(member.dateOfBirth) : '-'}</td>
                                <td className="px-4 py-3 text-gray-500 text-xs">{member.remarks || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                title="Nominees"
                icon={HeartHandshake}
                accent="bg-emerald-600"
                action={
                  <button
                    type="button"
                    onClick={() => setNomineeModal('add')}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add Nominee
                  </button>
                }
              >
                {nominees.length === 0 ? (
                  <EmptyState icon={HeartHandshake} title="No nominee records found" description="Add nominees for this pensioner using the button above." size="medium" />
                ) : (
                  <div className="space-y-3">
                    {nominees.map((nom) => (
                      <div key={nom.id} className="rounded-xl border border-slate-200 p-4">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <p className="font-semibold text-slate-900">{nom.nominee_name}</p>
                            <p className="text-sm text-slate-500">{nom.relationship} · {nom.benefit_percentage}% benefit</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${nom.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : nom.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                              {nom.status}
                            </span>
                            {nom.is_primary ? <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">Primary</span> : null}
                            <button
                              type="button"
                              onClick={() => setNomineeModal(nom)}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                              <Pencil className="h-3 w-3" /> Edit
                            </button>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 text-sm text-slate-600">
                          <div><span className="font-medium text-slate-700">DOB:</span> {nom.date_of_birth}</div>
                          <div><span className="font-medium text-slate-700">Bank:</span> {nom.bank_name || '-'}</div>
                          <div><span className="font-medium text-slate-700">Account:</span> {nom.account_number || '-'}</div>
                          <div><span className="font-medium text-slate-700">IFSC:</span> {nom.ifsc_code || '-'}</div>
                        </div>
                        {nom.status !== 'approved' ? (
                          <div className="mt-4 flex gap-2">
                            <button type="button" onClick={() => handleNomineeAction(nom.id, 'approve')} className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700">Approve</button>
                            <button type="button" onClick={() => handleNomineeAction(nom.id, 'reject')} className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100">Reject</button>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-6">
              <Employee360PensionDocs employeeId={profile.employee_id} />
            </div>
          )}
        </motion.div>
      </main>

      <AnimatePresence>
        {modalMode ? (
          <ActionModal
            mode={modalMode}
            profile={profile}
            employeeProfile={employeeProfile}
            nominees={nominees}
            familyDetails={familyDetails}
            loading={saving}
            onClose={() => {
              setModalMode('');
              searchParams.delete('action');
              setSearchParams(searchParams, { replace: true });
            }}
            onSubmit={runAction}
          />
        ) : null}
        {nomineeModal ? (
          <NomineeModal
            profileId={profile?.id}
            nominee={nomineeModal === 'add' ? null : nomineeModal}
            auth={auth}
            onClose={() => setNomineeModal(null)}
            onSaved={() => { setNomineeModal(null); load(); }}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p className="mt-1 text-sm font-medium text-slate-900">{value || '-'}</p>
  </div>
);

export default PensionerProfilePage;
